import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ServiceCenterPostResponseDto } from '@pro-sto/api-client';
import './PostFilter.css';

export interface PostFilterProps {
  posts: ServiceCenterPostResponseDto[];
  selectedPostUuids: string[];
  onChange: (postUuids: string[]) => void;
}

const FunnelIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.5 3.5H2.5L8.33333 10.3833V15.5L11.6667 17V10.3833L17.5 3.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const PostFilter: React.FC<PostFilterProps> = ({ posts, selectedPostUuids, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const sortedPosts = useMemo(
    () => [...posts].sort((a, b) => a.orderNumber - b.orderNumber),
    [posts]
  );

  const isActive = selectedPostUuids.length > 0;

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handlePostToggle = (postUuid: string) => {
    if (selectedPostUuids.includes(postUuid)) {
      onChange(selectedPostUuids.filter((uuid) => uuid !== postUuid));
    } else {
      onChange([...selectedPostUuids, postUuid]);
    }
  };

  const handleReset = () => onChange([]);

  if (sortedPosts.length === 0) {
    return null;
  }

  return (
    <div className="post-filter" ref={rootRef} data-testid="post-filter">
      <button
        type="button"
        className={`post-filter__toggle ${isActive ? 'post-filter__toggle_active' : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Фильтр по постам"
        aria-expanded={isOpen}
        data-testid="post-filter-toggle"
      >
        <FunnelIcon />
        {isActive && <span className="post-filter__badge">{selectedPostUuids.length}</span>}
      </button>

      {isOpen && (
        <div className="post-filter__dropdown" role="listbox" data-testid="post-filter-dropdown">
          <div className="post-filter__header">
            <span className="post-filter__title">Посты</span>
            {isActive && (
              <button type="button" className="post-filter__reset" onClick={handleReset}>
                Сбросить
              </button>
            )}
          </div>
          {sortedPosts.map((post) => {
            const isSelected = selectedPostUuids.includes(post.uuid);
            return (
              <div
                key={post.uuid}
                className={`post-filter__option ${isSelected ? 'post-filter__option_selected' : ''}`}
                onClick={() => handlePostToggle(post.uuid)}
                role="option"
                aria-selected={isSelected}
                data-testid={`post-filter-option-${post.orderNumber}`}
              >
                <span>Пост {post.orderNumber}</span>
                {isSelected && <span className="post-filter__check" aria-hidden="true">✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PostFilter;
